import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable } from 'rxjs';
import { PermissionService } from '../../../core/security';
import { HierarchyNodeLevelResponseDto } from '../../hierarchy';
import { LayoutDefinition, ReportColumnMap, ReportDefinition, ReportItemEntity } from '../reporting.model';
import { ReportKey } from '../reports/report.registry';
import { ColumnQueryState, ReportColumnConfig, ReportColumnDefinition, ReportColumnGroupDefinition } from './report-column.model';
import { REPORT_COLUMN_GROUP_REGISTRY, REPORT_COLUMN_REGISTRY, ReportColumnGroupKeys } from './report-column.registry';

export type columnFilterQueryParam = { property: string; value: string };

@Injectable({
    providedIn: 'root'
})
export class ReportColumnService {
    private httpClient = inject(HttpClient);
    private permissionService = inject(PermissionService);

    private isSysAdmin = this.permissionService.hasPermission('sysAdmin');

    getColumnMap(reportKey: ReportKey, useCompactColumns: boolean = true): Observable<ReportColumnMap> {
        return this.httpClient.get<ReportColumnMap>(`api/common/reporting/column-map?reportKey=${reportKey}&useCompactColumns=${useCompactColumns}`);
    }

    /**
     * Builds the final set of column definitions for the report grid.
     *
     * This combines:
     *  - The report's column definition,
     *  - The currently selected column layout,
     *  - And the global column registry definitions.
     *
     * The result is a fully-merged array of ReportColumnDefinition objects
     * with resolved visibility, order, grouping, pivoting, aggregation,
     * and the mapped data fields (via the report’s columnMap).
     *
     * This output is consumed by the grid and represents the
     * authoritative, ready-to-render column configuration for the report.
     */
    getColumnDefinitions(reportColumnDefinition: ReportColumnConfig[], activeLayout: ReportItemEntity<LayoutDefinition> | null, columnMap: ReportColumnMap, nodeLevels: HierarchyNodeLevelResponseDto[], includeAllColumns: boolean) {
        /* Merge each Column's base definition from the registry with its report-specific overrides */
        let columns = reportColumnDefinition.map((baseColumn) => {
            const layoutColumn = activeLayout === null ? baseColumn : activeLayout.itemDefinition?.columns.find((c) => c.key === baseColumn.key);

            let column: ReportColumnDefinition = {
                ...REPORT_COLUMN_REGISTRY[baseColumn.key],
                ...baseColumn.overrides,
                ...layoutColumn?.overrides
            };

            const isReportColumnDefinition = activeLayout === null;

            column = this.applyOrderLogic(baseColumn, layoutColumn, isReportColumnDefinition, column);
            column = this.applyRowGroupAndVisibleLogic(layoutColumn, column);
            column = this.applyAggregationLogic(column);
            column = this.applyPivotLogic(column);
            column = this.applyFieldMapping(column, columnMap);

            return column;
        });

        columns = this.processHierarchyColumns(columns, nodeLevels, includeAllColumns);

        return columns;
    }

    getColumnDefinitionsFromLayoutDefinition(reportColumnDefinition: ReportColumnConfig[], layoutDefinition: LayoutDefinition, columnMap: ReportColumnMap, nodeLevels: HierarchyNodeLevelResponseDto[], includeAllColumns: boolean) {
        /* Merge each Column's base definition from the registry with its report-specific overrides */
        let columns = reportColumnDefinition.map((baseColumn) => {
            const layoutColumn = layoutDefinition.columns.find((c) => c.key === baseColumn.key);

            let column: ReportColumnDefinition = {
                ...REPORT_COLUMN_REGISTRY[baseColumn.key],
                ...baseColumn.overrides,
                ...layoutColumn?.overrides
            };

            const isReportColumnDefinition = false;

            column = this.applyOrderLogic(baseColumn, layoutColumn, isReportColumnDefinition, column);
            column = this.applyRowGroupAndVisibleLogic(layoutColumn, column);
            column = this.applyAggregationLogic(column);
            column = this.applyPivotLogic(column);
            column = this.applyFieldMapping(column, columnMap);

            return column;
        });

        columns = this.processHierarchyColumns(columns, nodeLevels, includeAllColumns);

        return columns;
    }

    getColumnGroupDefinitions(reportDefinition: ReportDefinition) {
        const columnGroups = REPORT_COLUMN_GROUP_REGISTRY;

        const groupDefinitions: ReportColumnGroupDefinition[] = [];
        for (const group in columnGroups) {
            const groupKey = group as ReportColumnGroupKeys;
            const groupFromRegistry = columnGroups[groupKey];
            const groupFromReportDef = reportDefinition.reportColumnGroups?.find((g) => g.key === groupKey);
            groupDefinitions.push({ ...groupFromRegistry, ...groupFromReportDef?.overrides });
        }

        return groupDefinitions;
    }

    private allowedProperties: (keyof ColumnQueryState)[] = ['filterOperator', 'filterValue', 'sortDirection', 'sortIndex'];

    getColumnDefaultsFromQueryParams(queryParams: Params): Record<string, ColumnQueryState> {
        const queryFilterKeys = Object.keys(queryParams).filter((k) => k.startsWith('c_'));

        const columnQueryState: Record<string, ColumnQueryState> = {};

        /* Build a Map for each column */
        for (const key of queryFilterKeys) {
            const rawValue = queryParams[key];
            const withoutPrefix = key.substring(2);

            /* Split QueryParam by __: ColumnKey__Property. If no Property, then assume that's the rawValue */
            const [columnKey, property] = withoutPrefix.split('__');

            if (!this.allowedProperties.includes(property as keyof ColumnQueryState)) {
                continue;
            }

            if (!columnQueryState[columnKey]) {
                columnQueryState[columnKey] = {};
            }

            const column = columnQueryState[columnKey];

            switch (property) {
                case 'sortIndex':
                    column.sortIndex = Number(rawValue);
                    break;
                case 'sortDirection':
                    if (rawValue === 'asc' || rawValue === 'desc') {
                        column.sortDirection = rawValue;
                    }
                    break;
                default:
                    column[property as keyof ColumnQueryState] = rawValue;
            }
        }

        return columnQueryState;
    }

    /**
     * Uses the subscriber's SQL hierarchy levels to name report columns.
     * Levels without nodes are omitted unless a SysAdmin includes all columns.
     */
    private processHierarchyColumns(columns: ReportColumnDefinition[], nodeLevels: HierarchyNodeLevelResponseDto[], includeAllColumns: boolean) {
        /* Split the column array into two separate arrays: Hierarchy and Non-Hierarchy Columns */
        const processedColumns: ReportColumnDefinition[] = [];

        for (const c of columns) {
            /* Skip over Non-Hierarchy Columns */
            if (!c.hierarchyColumnOptions) {
                processedColumns.push(c);
                continue;
            }

            const hierarchyColumn = this.applyHierarchyData(c, nodeLevels, includeAllColumns);

            if (hierarchyColumn) {
                processedColumns.push(hierarchyColumn);
            }
        }

        return processedColumns;
    }

    private applyHierarchyData(column: ReportColumnDefinition, nodeLevels: HierarchyNodeLevelResponseDto[], includeAllColumns: boolean): ReportColumnDefinition | null {
        const options = column.hierarchyColumnOptions;

        if (!options) {
            return null;
        }

        let nodeLevel: HierarchyNodeLevelResponseDto | undefined;
        if (options?.levelSequence === 1) {
            nodeLevel = nodeLevels.find((n) => {
                return n.hierarchyTypeKey === options?.hierarchyType && n.isRoot;
            });
        } else if (options?.levelSequence === 5) {
            nodeLevel = nodeLevels.find((n) => n.hierarchyTypeKey === options?.hierarchyType && n.isLeaf);
        } else {
            nodeLevel = nodeLevels.find((n) => n.hierarchyTypeKey === options?.hierarchyType && n.hierarchyNodeLevelSequence === options.levelSequence && !n.isRoot && !n.isLeaf);
        }

        if (nodeLevel && nodeLevel.hierarchyNodeCount > 0) {
            return { ...column, header: nodeLevel.hierarchyNodeLevelName };
        }

        if (this.isSysAdmin && includeAllColumns) {
            return { ...column };
        }

        return null;
    }

    /**
     * Determines the final display order for a column by combining
     * the report column definition order and any overrides from the selected layout.
     *
     * Report column definition behavior:
     * - The report column definition's order is used as-is.
     *
     * Additional layouts:
     * - If the report column definition does not define an order, only the layout's
     *   order (if provided) is applied.
     * - If the report column definition defines an order, the layout's order overrides it.
     * - Otherwise, the report column definition order is multiplied by 100 so unused
     *   columns naturally fall below layout-specific ones.
     */
    private applyOrderLogic(baseColumn: ReportColumnConfig, layoutColumn: ReportColumnConfig | undefined, isReportColumnDefinition: boolean, column: ReportColumnDefinition) {
        if (isReportColumnDefinition) {
            return column; /* Report column definition uses its own order untouched. */
        }

        const baseOrder = baseColumn.overrides?.order;

        /* If the report column definition didn't define an order then only a layout can supply one. */
        if (baseOrder == null) {
            return { ...column, order: layoutColumn?.overrides?.order };
        }

        /* Layout order overrides, else base order * 100 */
        const order = layoutColumn?.overrides?.order ?? baseOrder * 100;

        return { ...column, order };
    }

    /**
     * Determines the final `visible` and `rowGroup` values for a column based on
     * both the report column definition and the selected layout.
     *
     * Rules:
     * - Report column definition uses only its column overrides.
     * - Additional layouts override visibility and rowGroup; if a column does not
     *   appear in the layout, it is treated as `visible = false` and `rowGroup = false`.
     *
     * Interaction rules:
     * - A column marked as `visible = false` must never be in the row group panel.
     * - A column marked as `rowGroup = true` must always be hidden in the grid,
     *   even if the layout says it is visible.
     *
     * This ensures layout visibility controls what appears on the report,
     * and row grouping behaves consistently with AG Grid expectations.
     */
    private applyRowGroupAndVisibleLogic(layoutColumn: ReportColumnConfig | undefined, column: ReportColumnDefinition) {
        return {
            ...column,
            visible: layoutColumn?.overrides?.visible ?? false, // rowGroup ? false : visible,
            rowGroup: layoutColumn?.overrides?.rowGroup ?? false // !visible ? false : rowGroup
        };
    }

    /**
     * Applies aggregation rules to the column definition.
     *
     * If the column is hidden, `aggFunc` is cleared so it does not appear
     * in the Values panel by default. Its default aggregation is instead
     * moved to `initialAggFunc`, allowing users to manually add it later.
     *
     * If the column is visible, `aggFunc` is applied normally. An undefined
     * `aggFunc` allows the column type's default aggregation to be used.
     */
    private applyAggregationLogic(col: ReportColumnDefinition) {
        const agg = col.aggFunc;
        const visible = col.visible;

        const aggFunc = visible ? agg : null;
        const initialAggFunc = !visible ? agg : undefined;

        return {
            ...col,
            aggFunc,
            initialAggFunc
        };
    }

    /**
     * Ensures pivot is only enabled when the column is visible.
     * If the column is hidden, `pivot` is forced to false so it does not
     * appear automatically in Pivot Mode.
     */
    private applyPivotLogic(col: ReportColumnDefinition) {
        const visible = col.visible;

        const pivot = visible ? (col.pivot ?? false) : false;
        return { ...col, pivot };
    }

    /**
     * Maps the column’s data field to the actual field name returned by the API.
     * If a custom `dataField` is provided, it is used; otherwise the column’s key
     * is assumed. The columnMap remaps this value when the API uses aliased fields.
     */
    private applyFieldMapping(col: ReportColumnDefinition, columnMap: ReportColumnMap) {
        const dataField = col.dataField ?? col.key;
        const field = columnMap[dataField] ?? dataField;

        return { ...col, field };
    }
}
